/* eslint-disable jsx-a11y/tabindex-no-positive */
import React, { useEffect, useRef, useState } from 'react';
import { Form } from '@unform/web';
import { useParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  FormGroup,
  Card,
  CardBody,
  Button,
  Label,
} from 'reactstrap';
import { toast } from 'react-toastify';

import PageHeader from '~/components/Common/PageHeader';
import Loading from '~/components/Loading';
import Input from '~/components/Inputs/Input';
import InputMask from '~/components/Inputs/InputMask';
import InputSelect from '~/components/Inputs/InputSelect';

import { maskBuilder } from '~/util/maskTelephone';
import history from '~/services/history';
import api from '~/services/api';

const PARTNER_MAP = {
  BEE: 'Bee Delivery',
  UBER: 'Uber',
  MOTTU: 'Mottu',
  LALAMOVE: 'Lalamove',
  VIDDY_DELIVERIES: 'Viddy Entregas',
  ENTREGA99: '99 Entregas',
  IFOOD_ENVIOS: 'iFood Envios',
  PROPRIA: 'Entrega Própria',
};

const normalize = s =>
  String(s || '')
    .trim()
    .toUpperCase();
const pretty = s =>
  PARTNER_MAP[normalize(s)] ||
  normalize(s)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
const toOption = code => ({ value: normalize(code), label: pretty(code) });

const VEHICLE_TYPES = [
  { value: 'Moto', label: 'Moto' },
  { value: 'Carro', label: 'Carro' },
  { value: 'Bicicleta', label: 'Bicicleta' },
];

// altura dos selects = inputs
const INPUT_H = 44;
const selectStyles = {
  control: base => ({ ...base, minHeight: INPUT_H, height: INPUT_H }),
  valueContainer: base => ({ ...base, paddingTop: 6, paddingBottom: 6 }),
  indicatorsContainer: base => ({ ...base, height: INPUT_H }),
  input: base => ({ ...base, margin: 0 }),
  singleValue: base => ({ ...base, margin: 0 }),
  placeholder: base => ({ ...base, margin: 0 }),
};

// Formata telefone BR para passar no BRPhone
function formatBRPhone(digits) {
  const d = String(digits || '').replace(/\D/g, '');
  if (d.length === 11)
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10)
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return null;
}

const EditBlockedCourier = () => {
  const formRef = useRef(null);
  const fetchedServicesRef = useRef(false);

  // Aceita param como :id ou :courier_id (use o que seu router estiver usando)
  const params = useParams();
  const courierId = params.courier_id || params.id;

  const [loading, setLoading] = useState(true);
  const [courier, setCourier] = useState(null);

  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(true);

  const [telephoneV, setTelephone] = useState('');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [selectedVehicleType, setSelectedVehicleType] = useState(null);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'Editar Entregador Bloqueado | Viddy';
  }, []);

  // Carrega entregador
  useEffect(() => {
    const loadCourier = async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/administrator/blacklist/${courierId}`);
        const c = data?.courier_blacklist || data; // compatibilidade de wrapper
        setCourier(c || null);
        setTelephone(c?.telephone || '');
      } catch (err) {
        history.goBack();
        if (err?.response?.data?.message)
          toast.error(err.response.data.message);
        else toast.error('Houve um problema ao carregar o entregador.');
      } finally {
        setLoading(false);
      }
    };
    loadCourier();
  }, [courierId]);

  // Carrega serviços de entrega (mesmo padrão da tela de venda)
  useEffect(() => {
    const loadDeliveryServices = async () => {
      try {
        setLoadingDeliveries(true);
        if (fetchedServicesRef.current) return;
        fetchedServicesRef.current = true;

        const [
          {
            data: { delivery_services },
          },
        ] = await Promise.all([api.get('/utils/available-delivery-services')]);

        const options = (delivery_services || [])
          .map(tag => toOption(tag))
          .filter(o => o.value)
          .sort((a, b) => a.label.localeCompare(b.label));

        setDeliveryOptions(options);
      } catch {
        setDeliveryOptions([toOption('BEE'), toOption('UBER')]);
      } finally {
        setLoadingDeliveries(false);
      }
    };
    loadDeliveryServices();
  }, []);

  // Seta os selects quando dados e opções estiverem disponíveis
  useEffect(() => {
    if (!courier) return;

    // Partner
    if (deliveryOptions.length) {
      const opt = deliveryOptions.find(
        o => o.value === normalize(courier.delivery_method)
      );
      setSelectedPartner(opt || null);
    }

    // Vehicle type
    if (courier.vehicle_type) {
      const vopt =
        VEHICLE_TYPES.find(v => v.value === courier.vehicle_type) || null;
      setSelectedVehicleType(vopt);
    }
  }, [courier, deliveryOptions]);

  const handleSubmit = async formData => {
    try {
      formRef.current?.setErrors({});

      const deliveryMethod =
        normalize(
          formData?.delivery_method?.value ||
            formData?.delivery_method ||
            selectedPartner?.value
        ) || null;

      if (!deliveryMethod) {
        toast.error('Selecione o parceiro de entrega.');
        return;
      }

      const name = String(formData?.name ?? courier?.name ?? '').trim();
      if (!name) {
        toast.error('Informe o nome do entregador.');
        return;
      }

      const telDigits = formData.telephone
        ? String(formData.telephone).replace(/\D/g, '')
        : String(telephoneV || '').replace(/\D/g, '');

      const telFormatted = formatBRPhone(telDigits);

      const vehicleType =
        formData?.vehicle_type?.value ||
        formData?.vehicle_type ||
        selectedVehicleType?.value ||
        undefined;

      setSubmitting(true);

      const payload = {
        name,
        delivery_method: deliveryMethod,
        courier_external_id: formData.courier_external_id
          ? String(formData.courier_external_id).trim()
          : undefined,
        vehicle_plate: formData.vehicle_plate
          ? String(formData.vehicle_plate).trim().toUpperCase()
          : undefined,
        vehicle_make: formData.vehicle_make
          ? String(formData.vehicle_make).trim()
          : undefined,
        vehicle_model: formData.vehicle_model
          ? String(formData.vehicle_model).trim()
          : undefined,
        vehicle_color: formData.vehicle_color
          ? String(formData.vehicle_color).trim()
          : undefined,
        vehicle_type: vehicleType,
      };

      // Só envia telephone se formatou corretamente (opcional)
      if (telFormatted) payload.telephone = telFormatted;

      await api.put(`/administrator/blacklist/${courierId}`, payload);

      toast.success('Alterações salvas com sucesso!');
      history.push('/entregadores-bloqueados');
    } catch (err) {
      if (err?.response?.data?.message) {
        toast.error(err.response.data.message, {
          autoClose: err?.response?.data?.duration || 5000,
        });
      } else if (err?.response?.data?.telephone) {
        const msg = Array.isArray(err.response.data.telephone)
          ? err.response.data.telephone.join(', ')
          : String(err.response.data.telephone);
        toast.error(msg);
      } else {
        toast.error(err.message || 'Erro ao salvar alterações.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-content">
      <Container className="container new-layout">
        {loading ? (
          <Loading />
        ) : (
          <>
            <PageHeader
              pageTitle="EDITAR ENTREGADOR BLOQUEADO"
              backTo="/entregadores-bloqueados"
              responsivePosition="start"
              desktopPosition="center"
            />

            <Form
              id="courier-edit-form"
              ref={formRef}
              onSubmit={handleSubmit}
              initialData={{
                name: courier?.name || '',
                courier_external_id: courier?.courier_external_id || '',
                telephone: courier?.telephone || '',
                vehicle_plate: courier?.vehicle_plate || '',
                vehicle_make: courier?.vehicle_make || '',
                vehicle_model: courier?.vehicle_model || '',
                vehicle_color: courier?.vehicle_color || '',
                vehicle_type: courier?.vehicle_type || '',
                delivery_method: courier?.delivery_method || '',
              }}
              noValidate
            >
              <Card className="card new-layout">
                <CardBody className="card-body new-layout">
                  <Row className="w-100 mx-0 font-size-20 font-weight-700 text-gray-700 my-4">
                    Dados do Entregador
                  </Row>

                  <Row className="w-100 mx-0">
                    <Col lg={6} className="px-0 pr-lg-2">
                      <Label>Parceiro de Entrega</Label>
                      <FormGroup>
                        <InputSelect
                          name="delivery_method"
                          placeholder={
                            loadingDeliveries
                              ? 'Carregando...'
                              : 'Selecione um parceiro'
                          }
                          options={deliveryOptions}
                          value={selectedPartner}
                          onChange={setSelectedPartner}
                          isDisabled={
                            loadingDeliveries || deliveryOptions.length === 0
                          }
                          styles={selectStyles}
                          classNamePrefix="viddy-select"
                          singlePath
                        />
                      </FormGroup>
                    </Col>
                    <Col lg={6} className="px-0 pl-lg-2">
                      <Label>ID Externo</Label>
                      <FormGroup>
                        <Input
                          name="courier_external_id"
                          placeholder="ID externo do parceiro"
                          type="text"
                          tabIndex={2}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="w-100 mx-0">
                    <Col lg={6} className="px-0 pr-lg-2">
                      <Label>Nome</Label>
                      <FormGroup>
                        <Input
                          name="name"
                          placeholder="Nome do entregador"
                          type="text"
                          tabIndex={3}
                        />
                      </FormGroup>
                    </Col>
                    <Col lg={6} className="px-0 pl-lg-2">
                      <Label>Telefone</Label>
                      <FormGroup>
                        <InputMask
                          mask={maskBuilder(telephoneV)}
                          maskChar={null}
                          defaultValue={telephoneV}
                          name="telephone"
                          placeholder="Telefone"
                          type="text"
                          onChange={e => setTelephone(e.target.value)}
                          tabIndex={4}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="w-100 mx-0 font-size-20 font-weight-700 text-gray-700 my-4">
                    Dados do Veículo
                  </Row>

                  <Row className="w-100 mx-0">
                    <Col lg={4} className="px-0 pr-lg-2">
                      <Label>Placa</Label>
                      <FormGroup>
                        <Input
                          name="vehicle_plate"
                          placeholder="Placa do veículo"
                          type="text"
                          tabIndex={5}
                        />
                      </FormGroup>
                    </Col>
                    <Col lg={4} className="px-0 px-lg-2">
                      <Label>Marca</Label>
                      <FormGroup>
                        <Input
                          name="vehicle_make"
                          placeholder="Marca"
                          type="text"
                          tabIndex={6}
                        />
                      </FormGroup>
                    </Col>
                    <Col lg={4} className="px-0 pl-lg-2">
                      <Label>Modelo</Label>
                      <FormGroup>
                        <Input
                          name="vehicle_model"
                          placeholder="Modelo"
                          type="text"
                          tabIndex={7}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="w-100 mx-0">
                    <Col lg={6} className="px-0 pr-lg-2">
                      <Label>Cor</Label>
                      <FormGroup>
                        <Input
                          name="vehicle_color"
                          placeholder="Cor"
                          type="text"
                          tabIndex={8}
                        />
                      </FormGroup>
                    </Col>
                    <Col lg={6} className="px-0 pl-lg-2">
                      <Label>Tipo</Label>
                      <FormGroup>
                        <InputSelect
                          name="vehicle_type"
                          placeholder="Selecione o tipo"
                          options={VEHICLE_TYPES}
                          value={selectedVehicleType}
                          onChange={setSelectedVehicleType}
                          styles={selectStyles}
                          classNamePrefix="viddy-select"
                          singlePath
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Row className="w-100 mx-0 my-4">
                <Col align="end" className="px-0">
                  <Button
                    className="new-layout-btn main"
                    color="primary"
                    size="lg"
                    type="submit"
                    tabIndex={9}
                    disabled={submitting}
                  >
                    {submitting ? 'Salvando...' : 'Salvar alterações'}
                  </Button>
                </Col>
              </Row>
            </Form>
          </>
        )}
      </Container>
    </div>
  );
};

export default EditBlockedCourier;
